import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { products } from '../data/storeData';
import Button from '../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

export default function DailyCarryBuilder() {
  const [selections, setSelections] = useState({ bag: null, wallet: null, accessory: null });
  const [activeStep, setActiveStep] = useState('bag');

  const steps = [
    { id: 'bag', title: '1. Base', desc: 'The Vessel' },
    { id: 'wallet', title: '2. Carry', desc: 'The Essential' },
    { id: 'accessory', title: '3. Detail', desc: 'The Accent' }
  ];

  const handleSelect = (category, product) => {
    setSelections(prev => ({ ...prev, [category]: product }));
    const currentIndex = steps.findIndex(s => s.id === category);
    if (currentIndex < 2) setActiveStep(steps[currentIndex + 1].id);
  };

  const { subtotal, discount, total, isComplete } = useMemo(() => {
    let sum = 0;
    if (selections.bag) sum += selections.bag.price;
    if (selections.wallet) sum += selections.wallet.price;
    if (selections.accessory) sum += selections.accessory.price;
    const complete = selections.bag && selections.wallet && selections.accessory;
    const disc = complete ? sum * 0.15 : 0;
    return { subtotal: sum, discount: disc, total: sum - disc, isComplete: complete };
  }, [selections]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-creamDark pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Progress Header */}
        <div className="text-center mb-16">
          <h1 className="font-serif text-4xl md:text-5xl text-navy mb-4">Artisan Carry Builder</h1>
          <p className="font-sans text-xs tracking-[0.25em] uppercase text-saddle">Hand-pick your heritage kit and save 15%</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 items-start">
          {/* Main Selection Area */}
          <div className="flex-grow lg:w-2/3 bg-cream p-6 md:p-10 border border-brass/20 shadow-sm">
            <div className="flex border-b border-brass/20 mb-10 overflow-x-auto scrollbar-hide">
              {steps.map(step => (
                <button 
                  key={step.id} 
                  onClick={() => setActiveStep(step.id)}
                  className={`flex-1 min-w-[140px] pb-6 px-4 text-center transition-all relative ${
                    activeStep === step.id ? 'text-saddle' : 'text-navy/40 hover:text-navy'
                  }`}
                >
                  <span className="block font-sans text-[10px] uppercase tracking-[0.3em] font-bold mb-1">{step.title}</span>
                  <span className="block font-serif italic text-sm truncate">{selections[step.id]?.name || step.desc}</span>
                  {activeStep === step.id && (
                    <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 w-full h-0.5 bg-saddle" />
                  )}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <AnimatePresence mode="wait">
                {products.filter(p => p.category === activeStep).map(product => (
                  <motion.div 
                    key={product.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    onClick={() => handleSelect(activeStep, product)}
                    className={`group cursor-pointer border p-4 transition-all duration-500 ${
                      selections[activeStep]?.id === product.id ? 'border-saddle bg-white ring-1 ring-saddle' : 'border-transparent bg-white/40 hover:border-brass/30'
                    }`}
                  >
                    <div className="aspect-square mb-6 overflow-hidden bg-creamDark">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                    </div>
                    <div className="flex justify-between items-center">
                      <h3 className="font-serif text-lg text-navy">{product.name}</h3>
                      <span className="font-sans font-bold text-saddle">${product.price}</span>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* Sticky Summary Sidebar */}
          <div className="w-full lg:w-1/3 lg:sticky lg:top-40">
            <div className="bg-navy text-cream p-8 md:p-10 border-t-[6px] border-brass shadow-2xl">
              <h2 className="font-serif text-2xl mb-8 border-b border-cream/10 pb-4 tracking-tight">Your Workshop Kit</h2>
              
              <div className="space-y-6 mb-10">
                {steps.map(step => (
                  <div key={step.id} className="flex justify-between items-start gap-4">
                    <div className="flex-grow">
                      <span className="block font-sans text-[9px] uppercase tracking-[0.3em] text-brass mb-1">{step.id}</span>
                      <span className={`font-serif italic text-sm ${!selections[step.id] ? 'text-cream/30' : 'text-cream'}`}>
                        {selections[step.id]?.name || 'Not Selected'}
                      </span>
                    </div>
                    {selections[step.id] && (
                      <span className="font-sans text-sm font-bold text-brass">${selections[step.id].price}</span>
                    )}
                  </div>
                ))}
              </div>

              <div className="border-t border-cream/10 pt-8 space-y-3 font-sans">
                <div className="flex justify-between text-xs tracking-widest uppercase text-cream/50">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-xs tracking-widest uppercase text-brass">
                    <span>Bundle Discount (15%)</span>
                    <span>-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-2xl font-serif pt-6 border-t border-cream/10 mt-6">
                  <span>Total</span>
                  <span className="text-brass">${total.toFixed(2)}</span>
                </div>
              </div>

              <Button 
                variant="accent" 
                className="w-full mt-10 py-5 text-[11px] font-bold"
                disabled={!isComplete}
              >
                {isComplete ? 'Add Custom Kit to Cart' : 'Select 3 Items to Save'}
              </Button>
              
              <p className="text-[9px] font-sans text-center mt-6 uppercase tracking-[0.2em] text-cream/30">
                Includes Lifetime Workshop Warranty
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}